<template>
  <div class="searchable-select" v-click-outside-ss="close">
    <input
      ref="input"
      type="search"
      :name="fieldName"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="open ? 'true' : 'false'"
      :class="inputClass"
      :value="open ? query : selectedLabel"
      :placeholder="open ? selectedLabel || placeholder : placeholder"
      :disabled="disabled"
      :required="required && !value"
      autocomplete="off"
      data-lpignore="true"
      data-form-type="other"
      data-1p-ignore
      data-bwignore
      @focus="openList"
      @click="openList"
      @input="onType($event.target.value)"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="pickHighlighted"
      @keydown.esc="close"
      @keydown.tab="close"
    />
    <span class="searchable-select__caret" aria-hidden="true">▾</span>
    <!-- Se mueve al <body> para que el overflow de un modal no la recorte -->
    <ul
      v-show="open"
      ref="list"
      class="searchable-select__list"
      :style="listStyle"
    >
      <li
        v-for="(option, index) in visibleOptions"
        :key="option.value"
        :class="{
          'searchable-select__item': true,
          'is-active': index === highlighted,
          'is-selected': option.value === value,
          'is-pinned': option.pinned
        }"
        @mousedown.prevent="pick(option)"
        @mouseenter="highlighted = index"
      >
        {{ option.label }}
      </li>
      <li
        v-if="!visibleOptions.length"
        class="searchable-select__item is-empty"
      >
        {{ noResultsText }}
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * Select con texto para filtrar las opciones. options: [{ value, label }].
 * pinnedOptions se muestran siempre al final, filtre lo que filtre
 * (p. ej. "Otro (escribir manualmente)").
 */
const LIST_MAX_HEIGHT = 256;
const LIST_GAP = 2;
const VIEWPORT_MARGIN = 8;
const normalize = text =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default {
  name: "SearchableSelect",
  directives: {
    clickOutsideSs: {
      bind(el, binding, vnode) {
        el.__clickOutsideSs__ = event => {
          const list = vnode.context.$refs.list;
          if (el.contains(event.target)) return;
          if (list && list.contains(event.target)) return;
          binding.value(event);
        };
        document.addEventListener("mousedown", el.__clickOutsideSs__);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el.__clickOutsideSs__);
        delete el.__clickOutsideSs__;
      }
    }
  },
  props: {
    value: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    pinnedOptions: { type: Array, default: () => [] },
    placeholder: { type: String, default: "" },
    noResultsText: { type: String, default: "—" },
    inputClass: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      query: "",
      highlighted: 0,
      listStyle: {}
    };
  },
  computed: {
    // Nombre sin pinta de dirección para que los gestores de contraseñas
    // (LastPass en particular) no lo tomen como campo a autocompletar
    fieldName() {
      return `ss-${this._uid}`;
    },
    selectedLabel() {
      const option = this.options.find(o => o.value === this.value);
      return option ? option.label : "";
    },
    visibleOptions() {
      const needle = normalize(this.query.trim());
      const filtered = needle
        ? this.options.filter(o => normalize(o.label).includes(needle))
        : this.options;
      return [
        ...filtered,
        ...this.pinnedOptions.map(o => ({ ...o, pinned: true }))
      ];
    }
  },
  mounted() {
    document.body.appendChild(this.$refs.list);
  },
  beforeDestroy() {
    this.stopTracking();
    const list = this.$refs.list;
    if (list && list.parentNode) list.parentNode.removeChild(list);
  },
  methods: {
    position() {
      const input = this.$refs.input;
      if (!input) return;
      const rect = input.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const above = rect.top - VIEWPORT_MARGIN;
      const openUp = below < 160 && above > below;
      const maxHeight = Math.min(LIST_MAX_HEIGHT, openUp ? above : below);
      this.listStyle = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${Math.max(maxHeight - LIST_GAP, 80)}px`,
        ...(openUp
          ? { bottom: `${window.innerHeight - rect.top + LIST_GAP}px` }
          : { top: `${rect.bottom + LIST_GAP}px` })
      };
    },
    startTracking() {
      window.addEventListener("scroll", this.position, true);
      window.addEventListener("resize", this.position);
    },
    stopTracking() {
      window.removeEventListener("scroll", this.position, true);
      window.removeEventListener("resize", this.position);
    },
    openList() {
      if (this.disabled || this.open) return;
      this.position();
      this.startTracking();
      this.open = true;
      this.query = "";
      const index = this.visibleOptions.findIndex(o => o.value === this.value);
      this.highlighted = index >= 0 ? index : 0;
      this.$nextTick(this.scrollToHighlighted);
    },
    close() {
      this.stopTracking();
      this.open = false;
      this.query = "";
    },
    onType(text) {
      this.query = text;
      if (!this.open) {
        this.position();
        this.startTracking();
      }
      this.open = true;
      this.highlighted = 0;
    },
    move(step) {
      if (!this.open) {
        this.openList();
        return;
      }
      const total = this.visibleOptions.length;
      if (!total) return;
      this.highlighted = (this.highlighted + step + total) % total;
      this.$nextTick(this.scrollToHighlighted);
    },
    pickHighlighted() {
      const option = this.visibleOptions[this.highlighted];
      if (option) this.pick(option);
    },
    pick(option) {
      this.$emit("input", option.value);
      this.close();
      this.$refs.input.blur();
    },
    scrollToHighlighted() {
      const list = this.$refs.list;
      const item = list && list.children[this.highlighted];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }
};
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
}

.searchable-select input {
  width: 100%;
  padding-right: 2rem;
}

/* type="search" trae una "x" nativa en WebKit que no queremos */
.searchable-select input::-webkit-search-cancel-button,
.searchable-select input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.searchable-select__caret {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 0.75rem;
  color: #6b7280;
}

.searchable-select__list {
  position: fixed;
  z-index: 10000;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
}

.searchable-select__item {
  padding: 0.4rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;
  color: #111827;
}

.searchable-select__item.is-active {
  background: #eff6ff;
}

.searchable-select__item.is-selected {
  font-weight: 600;
}

.searchable-select__item.is-pinned {
  border-top: 1px solid #e5e7eb;
  color: #4b5563;
  font-style: italic;
}

.searchable-select__item.is-empty {
  cursor: default;
  color: #9ca3af;
}
</style>
